// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

export type LogLevel = 'info' | 'warn' | 'error' | 'good';

interface Entry {
  at: string;
  level: LogLevel;
  message: string;
}

const ICONS: Record<LogLevel, string> = { info: '·', good: '✓', warn: '!', error: '✕' };

/**
 * The event drawer. It doubles as the tool's honesty surface: every network
 * request is announced here, with its full URL, before it is made.
 */
export class EventLog {
  private entries: Entry[] = [];

  constructor(
    private readonly drawer: HTMLElement,
    private readonly list: HTMLElement,
    private readonly toggle: HTMLElement,
  ) {}

  add(level: LogLevel, message: string): void {
    const at = new Date().toLocaleTimeString(undefined, { hour12: false });
    this.entries.push({ at, level, message });

    const li = document.createElement('li');
    li.className = `lvl-${level}`;
    const time = document.createElement('span');
    time.className = 'drawer-time';
    time.textContent = `[${at}] `;
    li.append(time, document.createTextNode(`${ICONS[level]} ${message}`));
    this.list.append(li);
    this.list.scrollTop = this.list.scrollHeight;
  }

  clear(): void {
    this.entries = [];
    this.list.replaceChildren();
  }

  get text(): string {
    return this.entries.map((e) => `[${e.at}] ${ICONS[e.level]} ${e.message}`).join('\n');
  }

  get isOpen(): boolean {
    return !this.drawer.hidden;
  }

  open(): void {
    this.drawer.hidden = false;
    this.toggle.setAttribute('aria-expanded', 'true');
  }

  close(): void {
    this.drawer.hidden = true;
    this.toggle.setAttribute('aria-expanded', 'false');
  }

  toggleOpen(): void {
    if (this.isOpen) this.close();
    else this.open();
  }
}
